import { forwardRef, type ButtonHTMLAttributes } from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  /** Renders a spinner and disables the button. Mirrors HTHD's `isLoading` prop. */
  readonly isLoading?: boolean;
  /** Stretches the button to fill its container — same effect as `ferro-btn--block`. */
  readonly block?: boolean;
}

/**
 * Primary call-to-action button.
 *
 * Prop signature (variant/size/isLoading/block) matches HTHD's
 * `customer-app/src/components/ui/Button.tsx` so ported pages can keep the
 * exact same JSX.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className = '',
      variant = 'primary',
      size = 'md',
      isLoading,
      block,
      children,
      disabled,
      type,
      ...props
    },
    ref,
  ) => {
    const variantClass = `ferro-btn--${variant}`;
    const sizeClass = size === 'md' ? '' : `ferro-btn--${size}`;
    const blockClass = block ? 'ferro-btn--block' : '';
    const fullClassName = [
      'ferro-btn',
      variantClass,
      sizeClass,
      blockClass,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <button
        ref={ref}
        type={type ?? 'button'}
        className={fullClassName}
        disabled={disabled || isLoading}
        aria-busy={isLoading || undefined}
        {...props}
      >
        {isLoading ? (
          <span className="ferro-btn__spinner" aria-hidden="true">
            <svg
              className="ferro-spinner ferro-spinner--inline"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="3"
                opacity="0.25"
              />
              <path
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </span>
        ) : null}
        {children}
      </button>
    );
  },
);

Button.displayName = 'Button';
